import { useState, useEffect } from "react";
import { sendFeedback, getAggregatedFeedback } from "../service/feedbackBlog";
import {
  ThumbsUp,
  ThumbsDown,
  Facebook,
  Instagram,
  Linkedin,
  Link as LinkIcon,
} from "lucide-react";
import { SiX } from "react-icons/si";
import { BlogCardProps } from "../types/types";

const BlogCard: React.FC<BlogCardProps> = ({ title, content, imageUrl }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [hasVoted, setHasVoted] = useState<"like" | "dislike" | null>(null);
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [buttonActive, setButtonActive] = useState(false);

  useEffect(() => {
    const fetchVotes = async () => {
      const allVotes = await getAggregatedFeedback();
      const feedback = allVotes.find(
        (fb: { title: string }) => fb.title === title
      );
      if (feedback) {
        setLikes(feedback.likes);
        setDislikes(feedback.dislikes);
      }
    };
    fetchVotes();

    const vote = localStorage.getItem(`vote-${title}`);
    if (vote === "like" || vote === "dislike") {
      setHasVoted(vote as "like" | "dislike");
    }
  }, [title]);

  const handleVote = async (action: "like" | "dislike") => {
    if (hasVoted) return;
    if (action === "like") setLikes((prev) => prev + 1);
    if (action === "dislike") setDislikes((prev) => prev + 1);
    await sendFeedback({ title, action });
    localStorage.setItem(`vote-${title}`, action);
    setHasVoted(action);
  };

  const preview = content.split(" ").slice(0, 10).join(" ") + "...";

  const handleBackdropClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (e.target === e.currentTarget) {
      setModalOpen(false);
    }
  };

  // URL et texte pour le partage
  const currentUrl = encodeURIComponent(window.location.href);
  const shareText = encodeURIComponent(title);
  const twitterShare = `https://twitter.com/intent/tweet?text=${shareText}&url=${currentUrl}`;
  const facebookShare = `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`;
  const linkedinShare = `https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}`;
  const instagramUrl = `https://www.instagram.com/`;

  // clic sur le bouNAUN → fond vert pendant 10 s
  const handleLinkClick = () => {
    setButtonActive(true);
    setTimeout(() => {
      setButtonActive(false);
    }, 10000);
  };

  return (
    <>
      {/* Card */}
      <article className="relative bg-[var(--color-secondary-light)]/20 backdrop-blur-sm p-6 shadow-lg hover:shadow-3xl hover:scale-[1.02] transition-transform duration-300 hover:-translate-y-2 flex flex-col justify-between w-full sm:w-2/3 md:w-[35%]">
        <h2 className="text-xl font-bold text-[var(--color-secondary)] mb-3 uppercase">
          {title}
        </h2>
        <p className="text-gray-900 text-sm leading-relaxed flex-1">
          {preview}
        </p>
        <button
          onClick={() => setModalOpen(true)}
          className="mt-4 text-xs text-[var(--color-secondary-light)] hover:text-[var(--color-primary-light)] hover:underline self-start"
        >
          Read More →
        </button>
        <div className="w-full max-w-3xl mx-auto px-1 mt-6 border-t pt-4">
          <div className="flex flex-col md:flex-row items-center justify-between ">
            {/* Like & Dislike */}
            <div className="flex items-center space-x-4">
              <button
                disabled={!!hasVoted}
                onClick={() => handleVote("like")}
                className={`rounded-full w-8 h-8 flex items-center justify-center transition-transform duration-150 active:scale-110 disabled:opacity-50 ${
                  hasVoted === "like"
                    ? "bg-green-600 text-white"
                    : "text-green-600 hover:bg-green-100"
                }`}
              >
                <ThumbsUp className="w-4 h-4" />
              </button>
              <span className="text-xs text-green-600 font-bold animate-pulse transition-transform duration-300 hover:scale-110">
                {likes}
              </span>
              <button
                disabled={!!hasVoted}
                onClick={() => handleVote("dislike")}
                className={`rounded-full w-8 h-8 flex items-center justify-center transition-transform duration-150 active:scale-110 disabled:opacity-50 ${
                  hasVoted === "dislike"
                    ? "bg-red-600 text-white"
                    : "text-red-600 hover:bg-red-100"
                }`}
              >
                <ThumbsDown className="w-4 h-4" />
              </button>
              <span className="text-xs text-red-600 font-bold animate-pulse transition-transform duration-300 hover:scale-110">
                {dislikes}
              </span>
            </div>

            {/* Share buttons */}
            <div className="flex items-center space-x-4">
              <a
                href={twitterShare}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full w-8 h-8 flex items-center justify-center text-sky-500 hover:bg-sky-100 transition-transform duration-150 active:scale-110"
              >
                <SiX className="w-4 h-4" />
              </a>
              <a
                href={facebookShare}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full w-8 h-8 flex items-center justify-center text-blue-700 hover:bg-blue-100 transition-transform duration-150 active:scale-110"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={linkedinShare}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-transform duration-150 active:scale-110"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full w-8 h-8 flex items-center justify-center text-pink-500 hover:bg-pink-100 transition-transform duration-150 active:scale-110"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <button
                onClick={handleLinkClick}
                className={`rounded-full w-8 h-8 flex items-center justify-center transition-transform duration-150 active:scale-110 ${
                  buttonActive
                    ? "bg-green-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <LinkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Modal */}
      {modalOpen && (
        <div
          onClick={handleBackdropClick}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn"
        >
          <div className="bg-[var(--color-primary-bg)] p-4 max-w-3xl w-full relative border border-[var(--color-secondary-light)]/40 shadow-xl animate-fadeInUp flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            {/* Left – Texte */}
            <div className="w-full md:w-1/2 flex flex-col justify-between space-y-4">
              <div>
                <h2 className="text-xl text-center font-bold text-[var(--color-secondary-dark)]">
                  {title}
                </h2>
                <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed text-justify p-5">
                  {content}
                </p>
              </div>
              {/* Modal Like & Dislike */}
              <div className="flex justify-end items-center space-x-4 pr-5 mb-2">
                <button
                  disabled={!!hasVoted}
                  onClick={() => handleVote("like")}
                  className={`rounded-full w-8 h-8 flex items-center justify-center transition-transform duration-150 active:scale-110 disabled:opacity-50 ${
                    hasVoted === "like"
                      ? "bg-green-600 text-white"
                      : "text-green-600 hover:bg-green-100"
                  }`}
                >
                  <ThumbsUp className="w-4 h-4" />
                </button>
                <span className="text-xs text-green-600 font-bold animate-pulse hover:scale-110 transition-transform duration-300">
                  {likes}
                </span>
                <button
                  disabled={!!hasVoted}
                  onClick={() => handleVote("dislike")}
                  className={`rounded-full w-8 h-8 flex items-center justify-center transition-transform duration-150 active:scale-110 disabled:opacity-50 ${
                    hasVoted === "dislike"
                      ? "bg-red-600 text-white"
                      : "text-red-600 hover:bg-red-100"
                  }`}
                >
                  <ThumbsDown className="w-4 h-4" />
                </button>
                <span className="text-xs text-red-600 font-bold animate-pulse hover:scale-110 transition-transform duration-300">
                  {dislikes}
                </span>
              </div>
            </div>

            {/* Right – Image */}
            <div className="w-full md:w-1/2 overflow-hidden">
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-60 md:h-full object-cover transition-transform duration-500 ease-in-out hover:scale-105"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogCard;
